import { useLayoutEffect, useRef, useState } from 'react'
import type { DiffSelection } from '../../hooks/useDiffSelection'

interface SelectionToolbarProps {
  selection: DiffSelection | null
  onAsk: (sel: DiffSelection) => void
  onComment: (sel: DiffSelection) => void
  onCopyLink: (sel: DiffSelection) => void
}

const TOOLBAR_OFFSET = 8
const MIN_VIEWPORT_MARGIN = 8

export function SelectionToolbar({ selection, onAsk, onComment, onCopyLink }: SelectionToolbarProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useLayoutEffect(() => {
    if (!selection || !ref.current) {
      setPos(null)
      return
    }
    const tb = ref.current.getBoundingClientRect()
    const { rect } = selection
    const tbWidth = tb.width || 240
    const tbHeight = tb.height || 32

    let top = rect.top - tbHeight - TOOLBAR_OFFSET
    if (top < MIN_VIEWPORT_MARGIN) {
      top = rect.bottom + TOOLBAR_OFFSET
    }
    let left = rect.left + rect.width / 2 - tbWidth / 2
    const maxLeft = window.innerWidth - tbWidth - MIN_VIEWPORT_MARGIN
    if (left > maxLeft) left = maxLeft
    if (left < MIN_VIEWPORT_MARGIN) left = MIN_VIEWPORT_MARGIN

    setPos({ top, left })
  }, [selection])

  if (!selection) return null

  const lineRangeLabel =
    selection.startLine === selection.endLine
      ? `L${selection.startLine}`
      : `L${selection.startLine}–L${selection.endLine}`

  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label="Selection actions"
      data-testid="selection-toolbar"
      onMouseDown={(e) => e.preventDefault()}
      className="fixed z-[300] flex items-center gap-px bg-raised border border-border-strong rounded-md shadow-[0_6px_28px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.04)] overflow-hidden font-mono text-[11px]"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <button
        type="button"
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-transparent border-0 text-fg hover:bg-overlay cursor-pointer"
        onClick={() => onAsk(selection)}
      >
        <span aria-hidden className="text-accent">⚡</span>
        <span>Ask AI · {lineRangeLabel}</span>
      </button>
      <span aria-hidden className="w-px h-4 bg-border-subtle" />
      <button
        type="button"
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-transparent border-0 text-fg hover:bg-overlay cursor-pointer"
        onClick={() => onComment(selection)}
      >
        <span aria-hidden>💬</span>
        <span>Comment</span>
      </button>
      <span aria-hidden className="w-px h-4 bg-border-subtle" />
      <button
        type="button"
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-transparent border-0 text-fg hover:bg-overlay cursor-pointer"
        onClick={() => onCopyLink(selection)}
        title="Copy link to this range"
      >
        <span aria-hidden>🔗</span>
        <span>Copy link</span>
      </button>
    </div>
  )
}
